import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as React from "react";
import { cn } from "@/core/utils/utils";

interface ScrollAreaProps
  extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  hideDefaultScrollBar?: boolean;
  viewportRef?: React.Ref<HTMLDivElement>;
  onViewportScroll?: React.UIEventHandler<HTMLDivElement>;
  /**
   * Scrolls vertically only, so content is held to the viewport's width.
   *
   * Radix sizes the viewport's content wrapper with an inline
   * `display: table; min-width: 100%`, which lets content wider than the viewport
   * size to itself — that is what makes horizontal scrolling work, and it is the
   * right default. In a vertical-only list it is wrong: the wrapper grows to its
   * widest child instead of to the viewport, so nothing inside ever has a width to
   * lay out against and `truncate` silently clips mid-word instead of ellipsizing.
   *
   * Pass this for lists and columns that only scroll down. Leave it off for
   * anything that scrolls sideways — a wide table, a sheet tab strip, a chart.
   */
  vertical?: boolean;
}

const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  ScrollAreaProps
>(function ScrollArea(
  {
    className,
    children,
    hideDefaultScrollBar,
    viewportRef,
    onViewportScroll,
    vertical,
    ...props
  },
  ref,
) {
  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      className={cn("relative overflow-hidden", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        onScroll={onViewportScroll}
        className={cn(
          "h-full w-full rounded-[inherit]",
          // `!` because Radix sets `display` inline on that wrapper.
          vertical && "[&>div]:!block",
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {!hideDefaultScrollBar && <ScrollBar />}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "z-20 flex touch-none select-none transition-colors",
      orientation === "vertical" &&
        "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" &&
        "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
